/**
 * Client-side validation of notification rules (dashboard rule editors).
 *
 * The backend serves its rule-args schema at
 * /v2/edit/notification_ruleset/rule_schema/ (backend
 * demo/lib/events/rule_schema.py); ./ruleSchema.ts — a verbatim copy of the
 * console's interpreter — runs it. Issues on new or changed rules block the save;
 * issues on rules saved unchanged are warnings, exactly how the server gates the
 * PUT (it answers 406 for the former and accepts the latter). A failed schema
 * fetch simply means no client-side validation; the server still checks.
 */
import * as React from 'react';

import { DataAPIContext, GET_ONE } from '../api';
import { nestedRulesetId, schemaSupported, validateRuleList } from './ruleSchema';

let schemaPromise = null;

/** The schema, fetched once per page load (null when unavailable). */
export function fetchRuleSchema(dataAPI) {
    if (!schemaPromise) {
        schemaPromise = Promise.resolve()
            .then(() => dataAPI(GET_ONE, 'notification_ruleset', { urlSuffix: 'rule_schema/' }))
            .then((res) => (res && res.statusCode === 200 && schemaSupported(res.data) ? res.data : null))
            .catch((err) => {
                console.warn('fetchRuleSchema failed', err);
                return null;
            });
    }
    return schemaPromise;
}

/** Tests only. */
export function resetRuleSchemaCache() {
    schemaPromise = null;
}

export function useRuleSchema() {
    const dataAPI = React.useContext(DataAPIContext);
    const [schema, setSchema] = React.useState(null);
    React.useEffect(() => {
        let active = true;
        fetchRuleSchema(dataAPI).then((s) => {
            if (active) setSchema(s);
        });
        return () => {
            active = false;
        };
    }, [dataAPI]);
    return schema;
}

function safeNestedId(raw) {
    try {
        return nestedRulesetId(raw);
    } catch (e) {
        return null;
    }
}

/** Stored rows (as last loaded/saved) by id, in the shape rowsEqual compares. */
export function baselineById(rules) {
    const out = {};
    (rules || []).forEach((r) => {
        if (r && typeof r.id === 'number') {
            out[r.id] = {
                type: r.type,
                tpl: r.tpl,
                args: r.args,
                field: r.field,
                negate: r.negate,
                op: r.op,
                value: r.value,
                nested_ruleset_id: safeNestedId(r.nested_ruleset),
            };
        }
    });
    return out;
}

const EMPTY = { byIdx: {}, blocking: false };

/**
 * `{byIdx: {[rule.idx]: {errors: [msg], warnings: [msg]}}, blocking}` for the
 * editor's rules (array order = what the PUT sends) against the stored ones.
 */
export function computeRuleIssues(schema, rules, baselineRules) {
    if (!schema || !Array.isArray(rules)) return EMPTY;
    const res = validateRuleList(schema, rules, baselineById(baselineRules));
    const byIdx = {};
    const put = (bucket, kind) => {
        Object.keys(bucket).forEach((i) => {
            const rule = rules[Number(i)];
            const key = rule && rule.idx !== undefined ? rule.idx : Number(i);
            byIdx[key] = byIdx[key] || { errors: [], warnings: [] };
            bucket[i].forEach((issue) => byIdx[key][kind].push(issue.message));
        });
    };
    put(res.errors, 'errors');
    put(res.warnings, 'warnings');
    return { byIdx, blocking: res.blocking };
}

/**
 * The 406's `errors[]` (`field: "rules.<i>.args.…"`) keyed by the `idx` of the
 * rule that was at position <i> in the array actually sent — rows get reordered
 * in the editor, so positions are not idx values.
 */
export function serverRuleIssues(errors, sentRules) {
    const byIdx = {};
    (errors || []).forEach((e) => {
        const m = /^rules\.(\d+)(?:\.|$)/.exec(e && e.field ? e.field : '');
        if (!m) return;
        const rule = (sentRules || [])[Number(m[1])];
        const key = rule && rule.idx !== undefined ? rule.idx : Number(m[1]);
        byIdx[key] = byIdx[key] || { errors: [], warnings: [] };
        byIdx[key].errors.push(e.message);
    });
    return byIdx;
}

/** Client issues and server errors for one rule, merged and de-duplicated. */
export function issuesForRule(idx, ...maps) {
    const errors = [];
    const warnings = [];
    maps.forEach((map) => {
        const entry = map && map[idx];
        if (!entry) return;
        entry.errors.forEach((m) => { if (errors.indexOf(m) < 0) errors.push(m); });
        entry.warnings.forEach((m) => { if (warnings.indexOf(m) < 0) warnings.push(m); });
    });
    return { errors, warnings };
}
